import { mergeSlotProps } from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import type { TextFieldProps as StandardTextFieldProps } from "@citrine/atoms/text-field";
import { TextField as StandardTextField } from "@citrine/atoms/text-field";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from "./form";
import { isInputEnterEvent } from "./utils";

export interface TextFieldRef {
  clear: (shouldValidate?: boolean) => void;
  setValue: (value: string | number, shouldValidate?: boolean) => void;
}

type TextFieldPropsWithoutRef<Shape extends FormikValues = FormikValues> = Omit<
  StandardTextFieldProps,
  "defaultValue" | "inputRef" | "name" | "ref" | "value"
> & {
  // prevent legacy ref callbacks
  inputRef?: React.RefObject<HTMLInputElement | null>;
  name: Paths<Shape, string> | Paths<Shape, number>;
  pattern?: RegExp;
  submitOnBlur?: boolean;
  submitOnEnter?: boolean;
  /**
   * Set to false to prevent form values from updating onChange, this helps prevent
   * rerendering if the TextField is part of a memoization dependent on the form values.
   **/
  updateFormValueOnChange?: boolean;
};

export type TextFieldProps<Shape extends FormikValues = FormikValues> =
  TextFieldPropsWithoutRef<Shape> & {
    ref?: React.ForwardedRef<TextFieldRef>;
  };

/**
 * we're using a regexp here instead of isNaN because a partially typed number may not be a number
 * examples:
 *   - "1e" is the start of an exponential number, but also NaN
 *   - "-" is the start of a negative number, but also NaN
 */
// eslint-disable-next-line no-useless-escape
const PARTIAL_NUMBER_REGEXP = /^[+\-]?[0-9]*\.?[0-9]*(e[+\-]?[0-9]*)?$/;

function TextFieldImpl<Shape extends FormikValues>(
  {
    name,
    pattern: inputPattern,
    slotProps,
    submitOnBlur,
    submitOnEnter: submitOnEnterProp,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    updateFormValueOnChange = true,
    ...props
  }: TextFieldPropsWithoutRef<Shape>,
  ref: React.ForwardedRef<TextFieldRef>,
) {
  const {
    id: formId,
    readonly,
    submitOnEnter: submitOnEnterContext,
  } = useFormConfiguration();
  const submitOnEnter = submitOnEnterProp ?? submitOnEnterContext;

  const [field, meta, helpers] = useField<string | number>(name);
  const { isSubmitting, submitForm, validateOnBlur, validateForm } =
    useFormikContext();
  const defaultRef = useRef<HTMLInputElement>(null);
  const inputRef = props.inputRef || defaultRef;

  const [isEditing, setIsEditing] = useState(false);

  // if it starts as a number; we keep it as a number by default
  const type =
    props.type ?? (typeof meta.initialValue === "number" ? "number" : "text");

  const isNumber = type === "number";

  const [internalValue, setInternalValue] = useState(field.value);

  // If the form value changes from outside, update the internal value.
  useEffect(() => {
    setInternalValue(Number.isNaN(field.value) ? "" : field.value);
  }, [field.value]);

  // wrap the formik setValue with a type conversions
  const setValue = useCallback(
    (value: string | number, shouldValidate?: boolean) => {
      const typedValue = isNumber
        ? value === ""
          ? NaN
          : Number(value)
        : String(value);

      setInternalValue(typedValue);
      return helpers.setValue(typedValue, shouldValidate);
    },
    [helpers, isNumber, setInternalValue],
  );

  // expose clear and setValue methods to the parent via ref
  useImperativeHandle(ref, () => ({
    clear(shouldValidate?: boolean) {
      setValue("", shouldValidate);
    },
    setValue,
  }));

  const pattern =
    inputPattern ?? (isNumber ? PARTIAL_NUMBER_REGEXP : undefined);

  const slotPropsMod = useMemo(
    () => ({
      ...slotProps,
      htmlInput: mergeSlotProps(slotProps?.htmlInput ?? {}, {
        form: formId,
        inputMode: isNumber ? ("decimal" as const) : undefined,
        pattern: pattern?.source.replace(/^\^/, "").replace(/\$$/, ""),
        // don't use number here to prevent spinboxes
        type: type === "number" ? "text" : type,
      }),
    }),
    [formId, isNumber, pattern?.source, slotProps, type],
  );

  // Rather than calling `setValue(e.target.value)` and `field.onBlur(e)`, call
  // `setValue(e.target.value, validateOnBlur)` and `field.setTouched(true, false)`
  // to avoid formik running validation onBlur as a side-effect of `setTouched`
  // with stale form values. This is particularly an issue for us with numeric
  // TextFields, where we rely on the `setValue` in this onBlur callback to convert
  // strings to numbers.
  //
  // For example if you rely on setTouched to do the validation, typing 0 in a
  // numeric TextField and blurring it will result in onBlur validation running
  // with "0" instead of 0.
  // Similarly clearing a numeric TextField and blurring it will result in
  // validation occurring using "" instead of NaN.
  //
  // See https://github.com/jaredpalmer/formik/issues/2083#issuecomment-571259235
  // for more info.

  // keep a ref toggle to skip form submission when blur is due to Escape key
  const hasEscaped = useRef<boolean>(false);
  const onBlurCallback = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      const wasEscaped = hasEscaped.current;
      // reset the toggle so we capture the next blur event
      hasEscaped.current = false;

      setIsEditing(false);

      onBlur?.(e);

      if (wasEscaped) {
        setValue(meta.initialValue ?? "", false);
      } else {
        // set possibly-number value
        setValue(e.target.value, false);
        helpers.setTouched(true, false);
      }

      // don't submit after Esc
      if (submitOnBlur && !wasEscaped) {
        await sleep(0);
        submitForm();
      } else if (validateOnBlur) {
        await sleep(0);
        validateForm();
      }
    },
    [
      helpers,
      meta.initialValue,
      onBlur,
      setValue,
      submitForm,
      submitOnBlur,
      validateForm,
      validateOnBlur,
    ],
  );

  const onChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (pattern && !pattern.test(e.target.value)) {
        // ignore invalid input
        return;
      }

      onChange?.(e);
      setInternalValue(e.target.value);

      if (updateFormValueOnChange) {
        if (isNumber) {
          if (isEditing) {
            // numbers should never be validated on change; it's still a string at this point
            helpers.setValue(e.target.value, false);
          } else {
            // box is not focused, so this change was due to autofill
            setValue(e.target.value, true);
            helpers.setTouched(true, false);
          }
        } else {
          field.onChange(e);
        }
      }
    },
    [
      field,
      helpers,
      isEditing,
      isNumber,
      onChange,
      pattern,
      setValue,
      updateFormValueOnChange,
    ],
  );

  const onFocusCallback = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const onKeyDownCallback = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);

      // Skip Escape handling if a parent (e.g., IntegratedRangeField) already
      // handled cancellation. Without this check, the blur handler resets the
      // field to its initial value, overwriting the parent's restored value.
      if (e.key === "Escape" && !e.defaultPrevented) {
        hasEscaped.current = true;
        inputRef.current?.blur();
      } else if (isInputEnterEvent(e)) {
        if (inputRef.current) {
          await setValue(inputRef.current.value, true);
        }
        if (submitOnEnter) {
          await submitForm();
        }
      }
    },
    [inputRef, onKeyDown, setValue, submitOnEnter, submitForm],
  );

  return (
    <StandardTextField
      {...props}
      disabled={props.disabled || readonly || isSubmitting}
      slotProps={slotPropsMod}
      inputRef={inputRef}
      name={name}
      // only show an error if the field has been touched
      error={meta.touched ? meta.error : undefined}
      type={type}
      value={Number.isNaN(internalValue) ? "" : (internalValue ?? "")}
      onBlur={onBlurCallback}
      onChange={onChangeCallback}
      onFocus={onFocusCallback}
      onKeyDown={onKeyDownCallback}
    />
  );
}

/**
 * The following are ref wrappers with explicit typing
 * (since forwardRef does not support generics)
 */

/**
 * TextField component should only be used within the context of a form/dialog
 * If you want a stand-alone text-field, use the one in the atoms section
 */
export const TextField = forwardRef(TextFieldImpl) as <
  Shape extends FormikValues = FormikValues,
>(
  props: TextFieldProps<Shape>,
) => ReturnType<typeof TextFieldImpl>;
